import * as React from 'react';
import { useId } from 'react';
import { cn } from './utils';
import './floating-label-input.css';

/**
 * FloatingLabelInput — text input with a floating label.
 *
 * At rest the label sits inside the input as a placeholder; on focus
 * or when a value is present, it lifts to the top-left and shrinks
 * to 75%. CSS-only — see `floating-label-input.css` for the mechanism.
 *
 * This primitive owns:
 *   - layout (wrapper + input + label + optional helper/error slot)
 *   - a11y wiring: auto-generated `id` via useId if none provided, so
 *     the `<label htmlFor>` link always resolves; `aria-describedby`
 *     merges any caller-provided value with the helper/error id
 *   - `aria-invalid` auto-set when `error` is passed (drives the
 *     danger outline + danger label colour via CSS)
 *
 * Use the `labelPosition="top"` escape hatch when the field renders
 * with a controlled value but you want the floating state forced
 * (e.g. programmatic fill where `:placeholder-shown` can't react
 * synchronously before the first paint).
 */

export interface FloatingLabelInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'placeholder' | 'prefix'> {
  /** Label text — floats above the input when focused or filled. */
  label: string;
  /** Error message — renders below in danger tone and sets aria-invalid. */
  error?: string;
  /** Helper text — renders below when no error is active. */
  helperText?: string;
  /** Force the floating state regardless of content/focus. */
  labelPosition?: 'floating' | 'top';
  /**
   * Read-only adornment rendered INSIDE the cell, left of the input text
   * (e.g. a derived prefix). Part of the same visual control — not editable
   * and not part of the typed value. The input + label are padded to clear it.
   */
  prefix?: React.ReactNode;
}

export const FloatingLabelInput = React.forwardRef<HTMLInputElement, FloatingLabelInputProps>(
  (
    { id, label, error, helperText, labelPosition = 'floating', prefix, className, ...props },
    ref,
  ) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    const messageId = useId();
    const message = error ?? helperText;
    const describedBy = message
      ? [messageId, props['aria-describedby']].filter(Boolean).join(' ')
      : props['aria-describedby'];
    const hasPrefix = prefix !== undefined && prefix !== null && prefix !== '';

    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        <div data-floating-label data-has-prefix={hasPrefix ? 'true' : undefined}>
          {hasPrefix ? (
            <span data-floating-prefix aria-hidden="true">
              {prefix}
            </span>
          ) : null}
          <input
            {...props}
            ref={ref}
            id={inputId}
            placeholder=" "
            aria-invalid={error ? true : props['aria-invalid']}
            aria-describedby={describedBy}
          />
          <label
            htmlFor={inputId}
            data-position={labelPosition === 'top' ? 'top' : undefined}
          >
            {label}
          </label>
        </div>
        {message ? (
          <span
            id={messageId}
            className={cn(
              'text-xs',
              error ? 'text-state-danger' : 'text-muted-foreground',
            )}
          >
            {message}
          </span>
        ) : null}
      </div>
    );
  },
);
FloatingLabelInput.displayName = 'FloatingLabelInput';
